import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { IPoll, PollType } from "@Interfaces/Entities/Poll";
import { postPollCreation } from "@Services/API/Polls";
import { UpdatePollRequest } from "@Services/API/Polls/interfaces";
import { ChannelActions } from "@Store";
import { NotificationActions } from "@Store";
import { generateNPSChoices } from "./utils/generateNPSChoices";
import {
    StyledFormContainer,
    StyledFieldGroup,
    StyledLabel,
    StyledQuestionField,
    StyledErrorMessage,
    StyledActionsContainer,
    StyledSubmitButton,
} from "./NpsForm.styled";

export interface NpsFormProps {
    channelId: string;
    onPollCreated: (poll: IPoll) => void;
    loading?: boolean;
    editingPoll?: IPoll;
}

const QUESTION_MAX_LENGTH = 140;
const QUESTION_MIN_LENGTH = 5;

/**
 * NpsForm Component
 *
 * Renders a form to create or edit NPS (0-10 scale) polls with:
 * - Question input (editable)
 * - Hard-coded scale labels (0: Colder/Less favorable, 10: Warmer/More favorable) - read-only
 * - Submit handler that creates or updates the poll
 *
 * SOLID: Single Responsibility - form handling only, delegates API calls to parent
 * Pattern: Follows PollTextField component design
 */
export const NpsForm: React.FC<NpsFormProps> = ({
    channelId,
    onPollCreated,
    loading: externalLoading = false,
    editingPoll,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<any>();
    const isEditMode = Boolean(editingPoll);

    // Form state
    const [question, setQuestion] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loading = externalLoading || isSubmitting;

    // Load poll data when in edit mode
    useEffect(() => {
        if (editingPoll) {
            setQuestion(editingPoll.question);
        }
    }, [editingPoll]);

    // Validation
    const questionError = useMemo(() => {
        if (!question.trim()) return null;
        if (question.length < QUESTION_MIN_LENGTH) {
            return t("dialogs.poll_creation.nps.question_too_short");
        }
        if (question.length > QUESTION_MAX_LENGTH) {
            return t("dialogs.poll_creation.nps.question_too_long");
        }
        return null;
    }, [question, t]);

    // Check if form is valid
    const isFormValid = useMemo(() => {
        return (
            question.trim().length >= QUESTION_MIN_LENGTH &&
            question.length <= QUESTION_MAX_LENGTH
        );
    }, [question]);

    // Submit handler
    const handleSubmit = useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();

            // Re-validate
            if (!isFormValid) {
                setError(t("dialogs.poll_creation.nps.validation_error"));
                return;
            }

            setIsSubmitting(true);
            setError(null);

            try {
                if (isEditMode && editingPoll) {
                    // For editing, only update the question
                    const payload: UpdatePollRequest = {
                        question: question.trim(),
                        poll_type: PollType.NPS,
                    };
                    await dispatch(
                        ChannelActions.editPoll(editingPoll._id, payload)
                    );
                    onPollCreated(editingPoll);
                } else {
                    // For creation, generate choices and create poll
                    const choices = generateNPSChoices();

                    // Create poll via API
                    const response = await postPollCreation({
                        question: question.trim(),
                        channel_id: channelId,
                        choices,
                        poll_type: PollType.NPS,
                    } as any);

                    // Notify parent
                    if (response.data) {
                        onPollCreated(response.data);

                        // Reset form
                        setQuestion("");
                    }
                }
            } catch (err) {
                console.error("Failed to create NPS poll:", err);
                setError(t("dialogs.poll_creation.nps.creation_error"));
                dispatch(
                    NotificationActions.notification({
                        snackbarOpen: true,
                        snackbarType: "error",
                        snackbarMessage: t(
                            "dialogs.poll_creation.nps.creation_error"
                        ),
                    })
                );
            } finally {
                setIsSubmitting(false);
            }
        },
        [
            isFormValid,
            question,
            isEditMode,
            editingPoll,
            channelId,
            onPollCreated,
            dispatch,
            t,
        ]
    );

    return (
        <>
            <StyledFormContainer onSubmit={handleSubmit} noValidate>
                {/* Error Alert */}
                {error && <StyledErrorMessage>{error}</StyledErrorMessage>}

                {/* Question Field */}
                <StyledFieldGroup>
                    <StyledLabel htmlFor="scala-question">
                        {t("dialogs.poll_creation.nps.question_label")} *
                    </StyledLabel>
                    <StyledQuestionField
                        id="scala-question"
                        variant="outlined"
                        placeholder={t(
                            "dialogs.poll_creation.nps.question_placeholder"
                        )}
                        value={question}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const newValue = e.target.value.slice(
                                0,
                                QUESTION_MAX_LENGTH
                            );
                            setQuestion(newValue);
                            setError(null); // Clear error on change
                        }}
                        error={!!questionError}
                        helperText={
                            <>
                                {questionError && (
                                    <>
                                        {questionError}
                                        <br />
                                    </>
                                )}
                                {`${question.length}/${QUESTION_MAX_LENGTH}`}
                            </>
                        }
                        disabled={loading}
                        fullWidth
                        multiline
                        rows={2}
                    />
                </StyledFieldGroup>

                {/* NPS Scale Display with Hard-coded Labels */}
                <StyledFieldGroup></StyledFieldGroup>

                {/* Submit Button */}
                <StyledActionsContainer>
                    <StyledSubmitButton
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={!isFormValid || loading}
                    >
                        {loading ? (
                            <>
                                <span style={{ marginRight: "0.5rem" }}>
                                    Creating...
                                </span>
                            </>
                        ) : (
                            t("dialogs.poll_creation.nps.ask_button")
                        )}
                    </StyledSubmitButton>
                </StyledActionsContainer>
            </StyledFormContainer>
        </>
    );
};

export default NpsForm;
